import { Effect, Schema } from 'effect';

/**
 * The shape of every identifier here, without an opinion on where it comes from.
 *
 * Named apart from {@link Id} because the constructor default is the whole difference between the two: an identifier we mint when nothing hands us one, and an identifier that is only ever read off something else. A calculation id is the latter — making one up would name a calculation that was never asked for.
 */
export const Uuid = Schema.NonEmptyString.check(Schema.isUUID());

/**
 * A code the engine wrote into a path, as a user reads it: `Gr%C3%A5` is `Grå`.
 *
 * For showing only. The code as written is what refers to the thing — what a path is built from and a parameter answered with — so it is kept as it came, and this is read off it rather than stored in its place: encoding it again need not give back what the engine wrote.
 *
 * A code that does not decode — a stray `%` the engine did not mean as an escape — is shown as it was written rather than not at all.
 */
export function decodeSegment(segment: string): string {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}

/**
 * What a thing is called when something else has to point at it later.
 *
 * A calculation is asked for and answered separately, so the answer has to find its way back to the position it was made for. Neither the place in the list nor the position object itself can carry that: the place is the user's to change, and the object is replaced every time a calculation lands on it. The id is what
 * survives both.
 *
 * Made here when nothing hands us one, which is what a constructor default does: it is applied when a position is built and not when one is decoded, so anything that arrives over the wire must still bring its own id rather than be given a new one.
 *
 * The default must stay Effect.sync. Effect.succeed would run crypto.randomUUID() once, when this module is loaded, and hand every position ever built the same id.
 */
export const Id = Uuid.pipe(Schema.withConstructorDefault(Effect.sync(() => crypto.randomUUID())));

/**
 * A whole number of one or more.
 *
 * Shared because a position is counted and measured by it, and the configuration made for that position carries the same measurements.
 */
export const PositiveInteger = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));
/**
 * What a position costs: nothing or more.
 *
 * Shared because the BOM engine's calculations are priced by the same rule, and the price it answers with is the one a position ends up carrying.
 *
 * Finite, so the three numbers JavaScript has that are not amounts are refused here rather than shown as one. NaN passes a greater-than check by failing it in both directions, and an infinity passes it outright; either would reach the screen as a price, and a row saying what a window costs must not say NaN.
 */
export const Price = Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0));

/**
 * How far along a calculation is.
 *
 * The one vocabulary for the state of a calculation, whether it is being read off a calculation the engine answered with or off the position it priced. A position has no status of its own: it is waiting until the engine has taken it on, being worked out while it has, and after that it is however the calculation went.
 * The first two both mean there is nothing to show yet.
 *
 * It lives here rather than beside either of them because both need it, and a Schema field needs the schema itself and not only its type — importing it either way round would put the two domain modules in a cycle they cannot be loaded out of.
 */
export const CalculationStatus = Schema.Literals(['pending', 'processing', 'succeeded', 'failed']);

/** How far along a calculation is, for anything that holds or presents one. */
export type ICalculationStatus = typeof CalculationStatus.Type;
